import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api, { fmtErr } from "../lib/api";
import { useCaptcha, CaptchaField } from "../components/Captcha";
import { LifeBuoy } from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const inputCls = "mt-1 w-full bg-white border border-neutral-300 rounded-md px-3 py-2 text-sm focus:ring-2 outline-none";

export default function PortalLogin() {
  const { slug } = useParams();
  const { login } = useAuth();
  const navigate = useNavigate();
  const [portal, setPortal] = useState(null);
  const [missing, setMissing] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const cap = useCaptcha();

  useEffect(() => {
    api.get(`/helpdesk/portal/${slug}`).then((r) => setPortal(r.data)).catch(() => setMissing(true));
  }, [slug]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(email, password, cap.payload);
      navigate(["hd_admin", "hd_agent", "hd_user"].includes(u.role) ? "/desk" : u.role === "author" ? "/dashboard" : "/admin");
    } catch (err) {
      setError(fmtErr(err));
      cap.refresh();
    } finally {
      setLoading(false);
    }
  };

  if (missing) {
    return (
      <div className="min-h-screen bg-[#F1F4F8] flex items-center justify-center px-4" data-testid="portal-not-found">
        <div className="max-w-md w-full bg-white border border-slate-200 rounded-2xl p-8 text-center">
          <LifeBuoy size={30} className="mx-auto text-slate-300" />
          <h1 className="text-xl font-bold font-heading mt-4">Portal not found</h1>
          <p className="text-sm text-slate-500 mt-2">This support portal doesn't exist or was moved.</p>
          <Link to="/login" className="inline-block mt-5 px-6 py-2 rounded-md text-white bg-neutral-900 hover:bg-neutral-800 text-sm font-medium">Go to standard login</Link>
        </div>
      </div>
    );
  }

  const brand = portal?.brand_color || "#10314b";
  return (
    <div className="min-h-screen flex items-center justify-center px-4" data-testid="portal-login-page"
      style={{ background: `linear-gradient(160deg, ${brand} 0%, ${brand} 38%, #F1F4F8 38.1%)` }}>
      <div className="w-full max-w-md">
        <div className="flex flex-col items-center mb-7">
          {portal?.logo_url ? (
            <img src={`${API_BASE}${portal.logo_url}`} alt={portal.name} data-testid="portal-logo"
              className="h-14 max-w-[220px] object-contain bg-white/95 rounded-xl px-4 py-2 shadow-sm" />
          ) : (
            <span className="w-12 h-12 rounded-xl bg-white/15 border border-white/25 flex items-center justify-center text-white"><LifeBuoy size={24} /></span>
          )}
          <h1 className="text-xl font-bold font-heading text-white mt-4" data-testid="portal-company-name" style={{ color: "#fff" }}>
            {portal?.name || "..."}
          </h1>
          <p className="text-xs text-white/70 mt-1">Support portal</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-2xl p-8 shadow-xl">
          <h2 className="text-lg font-semibold font-heading">Sign in</h2>
          <p className="text-sm text-slate-500 mt-0.5 mb-5">Access your tickets and knowledge base</p>
          {error && <div data-testid="portal-login-error" className="text-red-700 bg-red-50 border border-red-200 rounded-md p-3 text-sm mb-4">{error}</div>}
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="text-sm font-medium text-slate-700">Email</label>
              <input data-testid="portal-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} style={{ "--tw-ring-color": brand }} />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Password</label>
              <input data-testid="portal-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} style={{ "--tw-ring-color": brand }} />
            </div>
            <CaptchaField {...cap} testId="portal-captcha" />
            <button data-testid="portal-submit-button" disabled={loading}
              className="w-full py-2.5 rounded-md text-white font-medium text-sm transition-opacity hover:opacity-90 disabled:opacity-50"
              style={{ background: brand }}>
              {loading ? "Signing in..." : "Sign in"}
            </button>
          </form>
          <div className="flex justify-between mt-5 text-sm">
            <Link to="/forgot-password" className="text-slate-500 hover:text-slate-900">Forgot password?</Link>
            <Link to="/login" className="text-slate-400 hover:text-slate-700 text-xs self-center">Standard login</Link>
          </div>
        </div>
        <p className="text-center text-[11px] text-slate-400 mt-5">Powered by GloriDesk</p>
      </div>
    </div>
  );
}
